/**
 * Alert severity, said three ways at once: color, icon, and the word itself.
 * Two of the three states here are red and amber, so color alone is not an
 * option.
 */
import { InfoIcon, OctagonAlertIcon, TriangleAlertIcon } from 'lucide-react'
import { SEVERITY_LABELS, type AlertSeverity } from '@/lib/enums'
import { cn } from '@/lib/utils'

export interface SeverityBadgeProps {
  severity: AlertSeverity | string
  /** Overrides the word — use for a more specific label like "Overdue". */
  label?: string
  /** Icon-and-color only, for dense table cells. Still labeled for screen readers. */
  compact?: boolean
  className?: string
}

const STYLES: Record<AlertSeverity, string> = {
  info: 'border-cyan/30 bg-cyan/10 text-cyan',
  warning: 'border-amber/30 bg-amber/10 text-amber',
  critical: 'border-rose/40 bg-rose/10 text-rose',
}

const ICONS: Record<AlertSeverity, typeof InfoIcon> = {
  info: InfoIcon,
  warning: TriangleAlertIcon,
  critical: OctagonAlertIcon,
}

function normalize(severity: string): AlertSeverity {
  return severity === 'critical' || severity === 'warning' ? severity : 'info'
}

export function SeverityBadge({ severity, label, compact = false, className }: SeverityBadgeProps) {
  const level = normalize(String(severity))
  const Icon = ICONS[level]
  const text = label ?? SEVERITY_LABELS[level]

  return (
    <span
      className={cn(
        'inline-flex w-fit shrink-0 items-center gap-1 rounded-full border px-2 py-0.5',
        'font-mono text-[0.6875rem] tracking-wide uppercase',
        STYLES[level],
        compact && 'px-1.5',
        className,
      )}
    >
      <Icon className="size-3" aria-hidden />
      {compact ? <span className="sr-only">{text}</span> : text}
    </span>
  )
}
